import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import Table from '../../../core/common/dataTable/index';
import { fetchOwnerDashboard } from '../../../core/data/redux/service/OrganisationService';
import { formatError } from '../../../core/data/redux/service/AuthService';
import { AxiosResponse } from 'axios';
import Loader from '../../Loader/Loader';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/data/redux/store';
import { getGreeting } from '../../../core/data/utils';

type OwnerDashboardData = {
  totalOrganisations: number;
  activeOrganisations: number;
  inactiveOrganisations: number;
  suspendedOrganisations: number;
  trialOrganisations: number;
  fullOrganisations: number;
  totalUsers: number;
  activeUsers: number;
  totalTasks: number;
  organisations: Array<{
    orgCode: string;
    name: string;
    email: string;
    status: string;
    active: boolean;
    subscriptionType: string;
    trialEndDate?: string;
    userCount: number;
    activeUserCount: number;
    taskCount: number;
    maxUsers: number;
    maxTasks: number;
  }>;
};

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'badge-success';
    case 'SUSPENDED':
      return 'badge-warning';
    default:
      return 'badge-danger';
  }
};

const OwnerDashboard = () => {
  const [dashboard, setDashboard] = useState<OwnerDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUser = useSelector((state: RootState) => state.user.currentUser);

  useEffect(() => {
    fetchOwnerDashboard()
      .then((res: AxiosResponse) => setDashboard(res.data))
      .catch((error) => formatError(error?.response?.data))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { title: 'Organisation', dataIndex: 'name' },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Plan', dataIndex: 'subscriptionType' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: string) => (
        <span className={`badge ${statusBadgeClass(status)}`}>{status}</span>
      ),
    },
    { title: 'Users', render: (_: unknown, record: any) => `${record.activeUserCount}/${record.userCount}` },
    { title: 'Tasks', dataIndex: 'taskCount' },
    {
      title: 'Limits',
      render: (_: unknown, record: any) => {
        const users = record.maxUsers <= 0 ? 'Unlimited' : `${record.userCount}/${record.maxUsers}`;
        const tasks = record.maxTasks <= 0 ? 'Unlimited' : `${record.taskCount}/${record.maxTasks}`;
        return `${users} users, ${tasks} tasks`;
      },
    },
    {
      title: 'Manage',
      render: (_: unknown, record: any) => (
        <Link to="/owner/organisations" className="btn btn-sm btn-outline-primary">View</Link>
      ),
    },
  ];

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Platform Dashboard</h2>
            <p className="text-muted mb-0">
              {getGreeting()}, {currentUser?.name || 'Owner'} — track organisations, subscriptions, and usage.
            </p>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h6 className="text-muted mb-1">Total Organisations</h6>
                <h3 className="mb-0">{dashboard?.totalOrganisations || 0}</h3>
                <small className="text-success">{dashboard?.activeOrganisations || 0} active</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h6 className="text-muted mb-1">Subscriptions</h6>
                <h3 className="mb-0">{dashboard?.fullOrganisations || 0} FULL</h3>
                <small className="text-warning">{dashboard?.trialOrganisations || 0} on trial</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h6 className="text-muted mb-1">Platform Users</h6>
                <h3 className="mb-0">{dashboard?.activeUsers || 0}/{dashboard?.totalUsers || 0}</h3>
                <small className="text-muted">active / total</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h6 className="text-muted mb-1">Platform Tasks</h6>
                <h3 className="mb-0">{dashboard?.totalTasks || 0}</h3>
                <small className="text-danger">
                  {dashboard?.suspendedOrganisations || 0} suspended, {dashboard?.inactiveOrganisations || 0} inactive
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Organisation Overview</h5>
            <Link to="/owner/organisations" className="btn btn-primary btn-sm">Manage Organisations</Link>
          </div>
          <div className="card-body">
            <Table
              columns={columns}
              dataSource={dashboard?.organisations || []}
              rowKey="orgCode"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;
